import type { LocalStore } from '~/data/local-store'
import type { SyncKind, SyncRows } from '~/data/sync'
import type { Enrollment, TemplateVersion, WorkoutTemplate } from '~/data/templates'
import {
  recordsOf,
  type Exercise,
  type LoggedSet,
  type Measurements,
  type PendingOp,
  type Workout,
} from '~/data/types'

const DB_NAME = 'gymcloud'
/*
 * 2 added the template stores. 3 adds the versions runs follow, which is what lets a run of
 * somebody else's template plan with no connection. 4 gives every kind the change feed carries
 * a store of its own, and a place for the cursor beside them. Each upgrade adds what the
 * version before it lacks and takes nothing away.
 */
const DB_VERSION = 4

/** Written before there was a feed, so the row carries its own id. */
const ROW_STORES = [
  'sets',
  'workouts',
  'exercises',
  'templates',
  'enrollments',
  'versions',
] as const

/**
 * The rest of what the feed carries. Their keys are out of line: a weigh-in is keyed by its
 * day, a metric by a day and a kind together, a favourite by the key its food is known by —
 * none of which is a field called `id`.
 */
const KEYED_STORES = [
  'weighins',
  'metrics',
  'foods',
  'entries',
  'days',
  'goals',
  'favourites',
  'meals',
  'mealFavourites',
  'mealLogs',
  'settings',
] as const

const ALL_STORES = [...ROW_STORES, ...KEYED_STORES, 'ops', 'blobs', 'contexts', 'meta'] as const

type StoreName = (typeof ALL_STORES)[number]

/** Which store each kind of the feed lives in. */
const STORE_OF: Record<SyncKind, StoreName> = {
  set: 'sets',
  workout: 'workouts',
  template: 'templates',
  version: 'versions',
  enrollment: 'enrollments',
  weighin: 'weighins',
  metric: 'metrics',
  food: 'foods',
  entry: 'entries',
  day: 'days',
  goal: 'goals',
  favourite: 'favourites',
  meal: 'meals',
  mealFavourite: 'mealFavourites',
  mealLog: 'mealLogs',
  settings: 'settings',
}

const INLINE = new Set<StoreName>(ROW_STORES)

/** Where the cursor into the feed is kept, beside the rows it brought. */
const CURSOR_KEY = 'syncCursor'

/** Whether this browser has read the account through — see `loadAccountRead` on the store. */
const READ_KEY = 'accountRead'

function promisify<T>(request: IDBRequest<T>): Promise<T> {
  return new Promise((resolve, reject) => {
    request.onsuccess = () => {
      resolve(request.result)
    }
    request.onerror = () => {
      reject(request.error ?? new Error('IndexedDB request failed'))
    }
  })
}

function openDatabase(): Promise<IDBDatabase> {
  const request = indexedDB.open(DB_NAME, DB_VERSION)
  request.onupgradeneeded = () => {
    const db = request.result
    for (const name of ROW_STORES) {
      if (!db.objectStoreNames.contains(name)) {
        db.createObjectStore(name, { keyPath: 'id' })
      }
    }
    // Out-of-line auto-increment keys keep the queue FIFO; the op's own id is for removal.
    if (!db.objectStoreNames.contains('ops')) {
      db.createObjectStore('ops', { autoIncrement: true })
    }
    if (!db.objectStoreNames.contains('blobs')) {
      // Dormant since web logging went to the apps; kept so existing databases need no
      // version bump, and `clear` can sweep whatever old recordings were left in it.
      db.createObjectStore('blobs')
    }
    /*
     * What a template was last evaluated against, before a run's history was worked out from
     * the mirror. Nothing writes to it any more; it is still opened so `clear` can sweep
     * whatever a database made by an older build is holding in it.
     */
    if (!db.objectStoreNames.contains('contexts')) {
      db.createObjectStore('contexts')
    }
    for (const name of KEYED_STORES) {
      if (!db.objectStoreNames.contains(name)) {
        db.createObjectStore(name)
      }
    }
    // The cursor into the feed, and anything else about the mirror rather than in it.
    if (!db.objectStoreNames.contains('meta')) {
      db.createObjectStore('meta')
    }
  }
  return promisify(request)
}

/** The browser implementation of the local store. */
/**
 * A set cached by a version that pointed at a catalog row instead of carrying its own logging
 * method. It still knew what it recorded — under the exercise it pointed at — so the row is
 * carried across rather than dropped. Dropping would take unsent sets with it: a set logged
 * offline lives only here until its queued write goes out.
 */
type LegacySet = Omit<LoggedSet, 'records'> & {
  readonly records?: Measurements
  readonly exercise?: { readonly records: Measurements }
}

function withRecords(set: LegacySet): LoggedSet {
  const { exercise, records, ...rest } = set
  return { ...rest, records: records ?? exercise?.records ?? recordsOf(rest) }
}

/**
 * A run cached before runs followed versions. It has no `versionId`, and the sync path reads
 * that field to decide how to send a queued enrollment — so it is filled in as the empty
 * string, which is what a run started on this device and not yet sent carries anyway.
 *
 * A run cached before the cycle had an anchor has no `anchorDay` either. Null is the honest
 * answer for one: nobody was ever asked, so it keeps counting from the day it began.
 */
type LegacyEnrollment = Omit<Enrollment, 'versionId' | 'templateId' | 'anchorDay'> & {
  readonly versionId?: string
  readonly templateId?: string | null
  readonly anchorDay?: number | null
}

function withVersion(enrollment: LegacyEnrollment): Enrollment {
  return {
    ...enrollment,
    versionId: enrollment.versionId ?? '',
    templateId: enrollment.templateId ?? null,
    anchorDay: enrollment.anchorDay ?? null,
  }
}

export function createIndexedDbStore(): LocalStore {
  let database: Promise<IDBDatabase> | null = null

  async function store(name: StoreName, mode: IDBTransactionMode): Promise<IDBObjectStore> {
    database ??= openDatabase()
    return (await database).transaction(name, mode).objectStore(name)
  }

  async function loadAll<T>(name: StoreName): Promise<T[]> {
    return promisify((await store(name, 'readonly')).getAll() as IDBRequest<T[]>)
  }

  async function saveRow(name: StoreName, row: unknown): Promise<void> {
    await promisify((await store(name, 'readwrite')).put(row))
  }

  async function replaceRows(name: StoreName, rows: unknown[]): Promise<void> {
    const target = await store(name, 'readwrite')
    await promisify(target.clear())
    for (const row of rows) {
      target.put(row)
    }
    await new Promise<void>((resolve, reject) => {
      target.transaction.oncomplete = () => {
        resolve()
      }
      target.transaction.onerror = () => {
        reject(target.transaction.error ?? new Error('IndexedDB transaction failed'))
      }
    })
  }

  async function removeRow(name: StoreName, key: string): Promise<void> {
    await promisify((await store(name, 'readwrite')).delete(key))
  }

  return {
    loadSets: async () => (await loadAll<LegacySet>('sets')).map(withRecords),
    saveSet: (set) => saveRow('sets', set),
    replaceSets: (sets) => replaceRows('sets', sets),
    removeSet: (id) => removeRow('sets', id),

    loadWorkouts: () => loadAll<Workout>('workouts'),
    saveWorkout: (workout) => saveRow('workouts', workout),
    replaceWorkouts: (workouts) => replaceRows('workouts', workouts),
    removeWorkout: (id) => removeRow('workouts', id),

    loadExercises: () => loadAll<Exercise>('exercises'),
    replaceExercises: (exercises) => replaceRows('exercises', exercises),

    loadTemplates: () => loadAll<WorkoutTemplate>('templates'),
    saveTemplate: (template) => saveRow('templates', template),
    replaceTemplates: (templates) => replaceRows('templates', templates),
    removeTemplate: (id) => removeRow('templates', id),

    loadEnrollments: async () => (await loadAll<LegacyEnrollment>('enrollments')).map(withVersion),
    saveEnrollment: (enrollment) => saveRow('enrollments', enrollment),
    replaceEnrollments: (enrollments) => replaceRows('enrollments', enrollments),
    removeEnrollment: (id) => removeRow('enrollments', id),

    loadVersions: () => loadAll<TemplateVersion>('versions'),
    saveVersion: (version) => saveRow('versions', version),

    async loadRows<K extends SyncKind>(kind: K) {
      const name = STORE_OF[kind]
      const target = await store(name, 'readonly')
      const [keys, rows] = await Promise.all([
        // Every key here is a string: the in-line stores key on a row's own `id`, and the
        // rest are written under the id the feed gave them.
        promisify(target.getAllKeys() as IDBRequest<string[]>),
        promisify(target.getAll() as IDBRequest<SyncRows[K][]>),
      ])
      return new Map(rows.map((row, at) => [keys[at] ?? '', row]))
    },
    async putRow(kind, id, row) {
      const name = STORE_OF[kind]
      const target = await store(name, 'readwrite')
      await promisify(INLINE.has(name) ? target.put(row) : target.put(row, id))
    },
    removeRow: (kind, id) => removeRow(STORE_OF[kind], id),

    async loadCursor() {
      const held = await promisify(
        (await store('meta', 'readonly')).get(CURSOR_KEY) as IDBRequest<number | undefined>,
      )
      return held ?? 0
    },
    async saveCursor(seq) {
      await promisify((await store('meta', 'readwrite')).put(seq, CURSOR_KEY))
    },

    async loadAccountRead() {
      const held = await promisify(
        (await store('meta', 'readonly')).get(READ_KEY) as IDBRequest<boolean | undefined>,
      )
      return held === true
    },
    async saveAccountRead() {
      await promisify((await store('meta', 'readwrite')).put(true, READ_KEY))
    },

    loadOps: () => loadAll<PendingOp>('ops'),
    enqueueOp: async (op) => {
      await promisify((await store('ops', 'readwrite')).add(op))
    },
    /*
     * The ops store is keyed by auto-increment rather than by the op's own id, so writing one
     * back means finding it under whatever key it landed on. Same walk as removeOp, and the
     * same reason.
     */
    updateOp: async (next) => {
      const target = await store('ops', 'readwrite')
      const cursorRequest = target.openCursor()
      await new Promise<void>((resolve, reject) => {
        cursorRequest.onsuccess = () => {
          const cursor = cursorRequest.result
          if (!cursor) {
            resolve()
            return
          }
          if ((cursor.value as PendingOp).id === next.id) {
            cursor.update(next)
            resolve()
            return
          }
          cursor.continue()
        }
        cursorRequest.onerror = () => {
          reject(cursorRequest.error ?? new Error('IndexedDB cursor failed'))
        }
      })
    },
    removeOp: async (id) => {
      const target = await store('ops', 'readwrite')
      const cursorRequest = target.openCursor()
      await new Promise<void>((resolve, reject) => {
        cursorRequest.onsuccess = () => {
          const cursor = cursorRequest.result
          if (!cursor) {
            resolve()
            return
          }
          if ((cursor.value as PendingOp).id === id) {
            cursor.delete()
            resolve()
            return
          }
          cursor.continue()
        }
        cursorRequest.onerror = () => {
          reject(cursorRequest.error ?? new Error('IndexedDB cursor failed'))
        }
      })
    },

    clear: async () => {
      for (const name of ALL_STORES) {
        await promisify((await store(name, 'readwrite')).clear())
      }
    },
  }
}
